import React, { useRef, useEffect, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { DataGridComplete, DataGridRow } from 'components/DataGrid';
import Dropdown from 'components/Input/dropdown/Dropdown';
import { BoundingBox } from 'models/response';
import { useDispatch } from 'react-redux';
import actions from 'state/actions';

export interface CellDropdown {
  focus: boolean;
  onFocus: (cell?: { pageNumber: number; boundingBox: BoundingBox; content: string }) => void;
  row: DataGridRow;
  code: number;
  options: { value: string | number; label: string | number; confidence: number }[];
  gridRef?: any;
  disabled?: boolean;

  predictionsValuesBody: DataGridRow[];
}

const CellPredictionDropdown = ({
  focus,
  onFocus,
  row,
  code,
  options,
  gridRef,
  disabled,
  predictionsValuesBody,
}: CellDropdown) => {
  const selectRef = useRef<HTMLElement | null>(null);
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const [inputValue, setInputValue] = useState<string>('');
  const [value, setValue] = useState<{ value: string | number; label: string | number; confidence: number } | null>(
    null,
  );
  const dispatch = useDispatch();
  const fieldValue = predictionsValuesBody?.find(({ id }) => id === row.id);

  useEffect(() => {
    if (fieldValue) {
      const valueCode = fieldValue[code]?.content;
      const option = options?.find((e) => valueCode === e.value);
      if (option) {
        setValue({ label: option.label, value: option.value, confidence: option.confidence });
      }
    }
  }, [code, options, predictionsValuesBody, row.id, fieldValue]);

  useUpdateEffect(() => {
    if (focus) {
      selectRef.current?.focus();
    }
  }, [focus]);

  useEffect(() => {
    if (focus && !inputValue) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if ((isMac && event.metaKey && event.key === 'c') || (!isMac && event.ctrlKey && event.key === 'c')) {
          navigator.clipboard.writeText(typeof value === 'object' ? value?.label.toString() || '' : '');
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [focus, value, isMac, inputValue]);

  // Formatting and updating the rows state on inputs change
  const onFieldChange = (
    option: { value: string; label: string; confidence: number },
    rowId: string,
    fieldId: string | number,
  ) => {
    let actualPredictionBody: DataGridRow | null = null;
    const body = predictionsValuesBody?.map((elem: DataGridComplete) => {
      if (elem.id === rowId) {
        actualPredictionBody = {
          ...elem,
          [fieldId]: { content: option.value, confidence: option.confidence },
        };
        return actualPredictionBody;
      }
      return elem;
    });
    setValue(option);
    dispatch(actions.documents.setFormattedPredictionsData(body));
    dispatch(actions.documents.setSelectedPredictionsRow(actualPredictionBody));
  };

  return (
    <Dropdown
      id="cell-prediction-dropdown"
      onInputChange={(val) => setInputValue(val)}
      isClearable={focus}
      setRef={selectRef}
      value={value}
      options={options}
      onChange={(option) => onFieldChange(option, row.id, code)}
      onFocus={() => onFocus(fieldValue ? fieldValue[code] : null)}
      menuIsOpen={focus && !disabled}
      noBorder
      isPortal
      minWidthLabel="auto"
      closeMenuOnScroll={(event) => {
        if (event.target instanceof HTMLElement) {
          if (
            event.target?.className?.includes('MenuList') ||
            event.target?.className?.includes('ResizableTwoRowsLayout') ||
            document.querySelector('.react-select > div > div')?.className === event.target?.className ||
            event.target?.className?.includes('cell-text-input') ||
            event.target.parentElement?.className?.includes('menu') ||
            event.target.parentElement?.className?.includes('BodyContainer')
          ) {
            return false;
          }
          if (gridRef?.current) {
            gridRef.current.focusCell(null);
          }
          return true;
        }

        return true;
      }}
      disabled={disabled}
    />
  );
};

export default CellPredictionDropdown;
